import React, { useState, createContext, useContext, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  BACKEND_BASE_URL,
  COMMON_SUB_DOMAIN,
  EVES_SUB_SYS_DOMAIN,
  MASTER_SUB_DOMAIN,
  PARTNER_SUB_DOMAIN,
  PRODUCT_ORIGIN,
  SYS_ADMIN_CALLBACK_URL,
} from "@/config";
import { useDispatch } from "@/redux/store";
import { showErrorMessage } from "@/redux/apps/toast/ToastSlice";
import { getSubdomain } from "@/utils/common";
import subDomainClientMapping from "@/config/domainMapping";
import { decodeJwtWithJsrs, setCookie } from "@/utils";
import { hasRequiredOrganization } from "@/utils/helpers";

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthContext = createContext<any>(null);

export const useAuthContext = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const subDomain = getSubdomain();
  const { domain: inputDomainMap, clientId, redirectURL } = subDomainClientMapping(subDomain);
  const defaultSubDomain = subDomain !== "" && subDomain !== COMMON_SUB_DOMAIN ? subDomain : "xsm";

  // state
  const [authenticationToken, setAuthenticationToken] = useState<string>("");
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [authSessionKey, setAuthSessionKey] = useState<string>("");
  const [inputDomain, setInputDomain] = useState<string>(inputDomainMap || "");
  const [passwordError, setPasswordError] = useState<string>("");
  const [authTokenData, setAuthTokenData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  // update state for sso login
  const [state, setState] = useState<{ username: string; password: string }>({
    username: "",
    password: ""
  });

  //get login code and session data key from url
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const loginCode = searchParams.get("code") || "";
  const tokenRedirectUri = searchParams.get("tokenRedirectUri") || "";
  const hasProcessedLogin = useRef<string | null>(null);

  // XSM-1536 - SSO Feature
  // Author: Abhishek Bharti
  // Updated on 20/11/2025
  const handleSSOLogin = useCallback(
    async (code: string) => {
      if (code) {
        // Step 4: Call the API to exchange the code for an access token
        const redirectRequestUri = tokenRedirectUri
          ? window.atob(tokenRedirectUri)
          : (redirectURL as string);

        try {
          const tokenResponse = await fetch(`${BACKEND_BASE_URL}/xsm-api/v1/proxy/oauth2/token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              "Xsm-Domain": inputDomainMap as string,
              "Xsm-application": subDomain === (EVES_SUB_SYS_DOMAIN as string) ? "xsi" : "xsm",
              "xsm-iscompressed": "false",
              "XSM-Internal": "true",
              "X-Original-Origin": PRODUCT_ORIGIN
            },
            body: new URLSearchParams({
              client_id: clientId as string,
              grant_type: "authorization_code",
              redirect_uri: redirectRequestUri,
              code
            })
          });

          // Assuming the access token is available in the response
          const tokenData = await tokenResponse.json();

          const token = tokenData.access_token;

          if (token) {
            const adminAccounts = await fetchServiceAccount(token);
            setCookie("xsm_user_domain", inputDomainMap as string);

            localStorage.setItem(`${defaultSubDomain}_access_token`, token);
            localStorage.setItem(`${defaultSubDomain}_refresh_token`, tokenData?.refresh_token);
            localStorage.setItem(`${defaultSubDomain}_id_token`, tokenData?.id_token);
            // Saving Expiry Token
            const expiresInMs = tokenData.expires_in * 1000; // convert to milliseconds
            const expirationTime = Date.now() + expiresInMs;
            localStorage.setItem(`${defaultSubDomain}_expires_in`, expirationTime.toString());
            // check if email is of admin
            if (
              MASTER_SUB_DOMAIN === subDomain &&
              adminAccounts &&
              adminAccounts.includes(state.username)
            ) {
              navigate("/dashboard/application-creation");
            } else {
              // normal user flow
              setAuthenticationToken(token);
              await navigateUserToDashboard(token, tokenData, inputDomainMap as string);
            }
          } else {
            dispatch(showErrorMessage("Token is invalid"));
          }
        } catch (error) {
          console.error("handleLogin error", error);
        }
      } else {
        dispatch(showErrorMessage("Invalid code. Please try again."));
      }
    },
    [state.username, inputDomainMap, clientId, redirectURL, dispatch]
  );

  useEffect(() => {
    if (loginCode && hasProcessedLogin.current !== loginCode) {
      hasProcessedLogin.current = loginCode;
      handleSSOLogin(loginCode);
    }
  }, [loginCode, handleSSOLogin]);

  const loginFormik = useFormik({
    initialValues: {
      email: "",
      password: ""
    },
    onSubmit: () => { },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required")
        .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid Email")
    })
  });

  const otpFormik = useFormik({
    initialValues: {
      first: null,
      second: null,
      third: null,
      four: null,
      five: null,
      six: null
    },
    onSubmit: () => { },
    validationSchema: Yup.object({})
  });

  const { values: otpValues } = otpFormik;

  // Fetch user data
  const fetchUser = async (token: string, userName: string, domainName: string) => {
    const userResponse = await fetch(
      `${BACKEND_BASE_URL}/xsm-api/v1/user?userName=${userName}&domainName=${domainName}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "XSM-Domain": domainName,
          "XSM-Internal": "true",
          "X-Original-Origin": PRODUCT_ORIGIN
        }
      }
    );
    const response = await userResponse.json();
    const userData = response?.result?.users[0];
    return userData;
  };

  // Fetch service account
  const fetchServiceAccount = async (token: string) => {
    const domainResponse = await fetch(`${BACKEND_BASE_URL}/xsm-api/v1/admin/service-account`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "XSM-Domain": "master"
      }
    });
    const response = await domainResponse.json();
    const serviceAccounts = response?.data;
    return serviceAccounts;
  };

  // Fetch domain
  const fetchDomain = async (token: string, userName: string, domainName: string) => {
    const domainResponse = await fetch(
      `${BACKEND_BASE_URL}/xsm-api/v1/domain?userName=${userName}&domainName=${domainName}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "XSM-Domain": domainName
        }
      }
    );
    const response = await domainResponse.json();
    const domainData = response?.result?.domains[0];
    return domainData;
  };

  // Navigate user to dashboard
  const navigateUserToDashboard = async (authToken: string, tokenData: any, xsmDomain: string) => {
    if (authToken) {
      const decodedToken = decodeJwtWithJsrs(authToken);
      const userEmail = decodedToken?.sub;

      //step1: check mfa verification
      const isMFAVerified = await mfaVerification(authToken, xsmDomain);
      if (isMFAVerified && Object.values(isMFAVerified).length > 0) {
        setAuthTokenData(tokenData);
        //Fetch user
        const userResponse = await fetchUser(authToken, userEmail, xsmDomain);
        const organizationData = userResponse?.organizations || [];
        localStorage.setItem("xsm_organizations", JSON.stringify(organizationData));
        console.log("userResponse ==>", userResponse, organizationData);

        // Check if user has required organization data for PARTNER_SUB_DOMAIN or MASTER_SUB_DOMAIN
        if (
          (subDomain === PARTNER_SUB_DOMAIN ||
            subDomain === MASTER_SUB_DOMAIN) &&
          !hasRequiredOrganization(subDomain, organizationData)
        ) {
          setLoading(false);
          navigate("/unauthorized");
          return;
        }

        if (isMFAVerified.auth_type === "S") {
          await mobile2FAVerification(userResponse, authToken);
        } else if (isMFAVerified.auth_type === "E") {
          await email2FAVerification(userResponse, authToken);
        } else {
          setLoading(false);
          if (subDomain === EVES_SUB_SYS_DOMAIN) {
            window.location.assign(SYS_ADMIN_CALLBACK_URL);
            return;
          } else if (subDomain === PARTNER_SUB_DOMAIN) {
            navigate("/landing");
          } else if (subDomain === MASTER_SUB_DOMAIN) {
            navigate("/dashboard/application-creation");
          } else {
            navigate("/apps");
          }
        }
      }
    }
  };

  // Send SMS for Mobile 2FA
  const sendMobile2FAVerification = async (
    token: string,
    mobileNumber: string,
    domainName: string
  ) => {
    const verificationResponse = await fetch(
      `${BACKEND_BASE_URL}/xsm-api/v1/admin/2fa/verifications`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "XSM-Domain": domainName,
          "XSM-Internal": "true",
          "X-Original-Origin": PRODUCT_ORIGIN
        },
        body: JSON.stringify({
          channel: "sms",
          mobile: mobileNumber
        })
      }
    );
    const response = await verificationResponse.json();
    return response;
  };

  // Mobile 2FA Verification
  const mobile2FAVerification = async (userResponse: any, authToken: string) => {
    //trim mobile
    const mobileNumber = userResponse?.mobile_number.replace(/\s/g, "");

    //Send auth verification
    const verification = await sendMobile2FAVerification(authToken, mobileNumber, inputDomain);

    if (verification?.isSuccess) {
      setMobileNumber(mobileNumber);
      // redirect to mobile OTP page
      navigate(`/mobile-otp`);
    }
  };

  // Email Verification
  const sendEmail2FAVerification = async (token: string, email: string, domainName: string) => {
    const verificationResponse = await fetch(
      `${BACKEND_BASE_URL}/xsm-api/v1/admin/2fa/verifications`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "XSM-Domain": domainName
        },
        body: JSON.stringify({
          channel: "email",
          email: email
        })
      }
    );
    const response = await verificationResponse.json();
    return response;
  };

  // Email 2FA Verification
  const email2FAVerification = async (userResponse: any, authToken: string) => {
    //Send auth verification
    const verification = await sendEmail2FAVerification(authToken, userResponse.email, inputDomain);

    if (verification.isSuccess) {
      setEmailAddress(userResponse.email);
      //redirect to
      navigate(`/email-otp`);
    } else {
      setPasswordError("Problem in sending OTP");
    }
  };

  // Checking Multi Factor Authentication
  const mfaVerification = async (token: string, domainName: string) => {
    const mfaResponse = await fetch(
      `${BACKEND_BASE_URL}/xsm-api/v1/mfaauthpolicy?dom_name=${domainName}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "XSM-Domain": domainName,
          "XSM-Internal": "true",
          "X-Original-Origin": PRODUCT_ORIGIN
        }
      }
    );
    const contentType = mfaResponse.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const response = await mfaResponse.json();
      const mfaAuthData = response?.result;
      return mfaAuthData;
    } else {
      dispatch(showErrorMessage("Content-Type is not valid"));
      return null;
    }
  };

  const code = `${otpValues.first}${otpValues.second}${otpValues.third}${otpValues.four}${otpValues.five}${otpValues.six}`;

  // Verification Response
  const verificationResponse = async (otpType: string) => {
    const response = await fetch(`${BACKEND_BASE_URL}/xsm-api/v1/admin/2fa/verificationcheck`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authenticationToken}`,
        "XSM-Domain": inputDomain
      },
      body: JSON.stringify({
        ...(otpType === "email" && {
          email: emailAddress
        }),
        ...(otpType === "mobile" && {
          mobile: mobileNumber
        }),
        code
      })
    });
    return response.json();
  };

  const values = {
    authenticationToken,
    mobileNumber,
    setAuthenticationToken,
    setMobileNumber,
    authSessionKey,
    setAuthSessionKey,
    inputDomain,
    setInputDomain,
    setEmailAddress,
    emailAddress,
    navigateUserToDashboard,
    loginFormik,
    otpFormik,
    verificationResponse,
    loading,
    setLoading,
    authTokenData,
    appProducts: [],
    fetchServiceAccount,
    setState,
    state
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};
